import { z } from "zod";
import { displayValue } from "../utils/format.js";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const getRunActionsInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID)"),
  runId: z.string().describe("The run ID (GUID)"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
  failedOnly: z
    .boolean()
    .optional()
    .describe("If true, only return failed actions (default: false)"),
  includeInputs: z
    .boolean()
    .optional()
    .describe("If true, fetch and display input data for each action (default: false for succeeded, true for failed)"),
  includeOutputs: z
    .boolean()
    .optional()
    .describe("If true, fetch and display output data for each action (default: false for succeeded, true for failed)"),
  actionName: z
    .string()
    .optional()
    .describe("Filter to a specific action by name (partial match). Use to drill into one action's details."),
});

export type GetRunActionsInput = z.infer<typeof getRunActionsInputSchema>;

export const getRunActionsTool = {
  name: "get_run_actions",
  description:
    "Get detailed action-level information for a flow run. Shows each action's status, timing, inputs/outputs, and error details. Use 'failedOnly: true' to focus on failures, 'actionName' to drill into a specific action, 'includeInputs'/'includeOutputs' to see full data payloads.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID (GUID)",
      },
      runId: {
        type: "string",
        description: "The run ID (GUID)",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
      failedOnly: {
        type: "boolean",
        description: "If true, only return failed actions (default: false)",
      },
      includeInputs: {
        type: "boolean",
        description:
          "If true, fetch and display input data for each action (default: false for succeeded, true for failed)",
      },
      includeOutputs: {
        type: "boolean",
        description:
          "If true, fetch and display output data for each action (default: false for succeeded, true for failed)",
      },
      actionName: {
        type: "string",
        description:
          "Filter to a specific action by name (partial match). Use to drill into one action's details.",
      },
    },
    required: ["flowId", "runId"],
  },
};

export interface RunActionResult {
  name: string;
  status: string;
  startTime: string;
  endTime?: string;
  durationMs?: number;
  error?: {
    code: string;
    message: string;
  };
  inputs?: string;
  outputs?: string;
  retryCount?: number;
  trackedProperties?: Record<string, unknown>;
}

const TRUNCATE_LIMIT = 1000;
const ERROR_TRUNCATE_LIMIT = 500;

/**
 * Derive an error from a failed action's fetched outputs.
 *
 * The platform's run-action record populates `error` for SKIPS
 * (ActionConditionFailed) but leaves it NULL for many real connector
 * failures — the actual error lives only in the outputs body (observed
 * live 2026-08-08: a SharePoint 400 with error:null while four skipped
 * actions all carried error objects). Since outputs are already fetched
 * for failed actions, surface the real failure instead of "error: null".
 * Shapes seen in the wild: { statusCode, body } and { status, message }.
 */
function deriveErrorFromOutputs(data: unknown): { code: string; message: string } | undefined {
  if (!data || typeof data !== "object") return undefined;
  const d = data as Record<string, unknown>;
  const rawStatus = d["statusCode"] ?? d["status"];
  const status =
    typeof rawStatus === "number" || typeof rawStatus === "string" ? rawStatus : undefined;
  let message: string | undefined;
  if (typeof d["message"] === "string") {
    message = d["message"];
  } else if (d["body"] && typeof d["body"] === "object") {
    const body = d["body"] as Record<string, unknown>;
    const bodyError = body["error"] as Record<string, unknown> | undefined;
    message =
      typeof body["message"] === "string"
        ? (body["message"])
        : typeof bodyError?.["message"] === "string"
          ? (bodyError["message"])
          : JSON.stringify(body);
  } else if (typeof d["body"] === "string") {
    message = d["body"];
  }
  if (status === undefined && message === undefined) return undefined;
  return {
    code: status !== undefined ? `HTTP ${String(status)}` : "Failed",
    message: message ?? "(no error body in outputs)",
  };
}

function formatResourceData(data: unknown): string {
  if (data && typeof data === "object" && "body" in data) {
    const body = (data).body;
    const str = typeof body === "object" && body !== null ? JSON.stringify(body, null, 2) : displayValue(body);
    return str.length > TRUNCATE_LIMIT ? str.slice(0, TRUNCATE_LIMIT) + "…" : str;
  }
  const str = JSON.stringify(data, null, 2);
  return str.length > TRUNCATE_LIMIT ? str.slice(0, TRUNCATE_LIMIT) + "…" : str;
}

export async function handleGetRunActions(
  api: FlowManagementApi,
  input: unknown
): Promise<{ text: string; structured?: Record<string, unknown> }> {
  const parsed = getRunActionsInputSchema.parse(input);

  logger.info({ input: parsed }, "Executing get_run_actions");

  const actions = await api.getFlowRunActions(
    parsed.flowId,
    parsed.runId,
    parsed.environment
  );

  let filteredActions = actions;

  if (parsed.actionName) {
    const needle = parsed.actionName.toLowerCase();
    filteredActions = filteredActions.filter((a) =>
      a.name.toLowerCase().includes(needle)
    );
  }

  if (parsed.failedOnly) {
    filteredActions = filteredActions.filter(
      (a) =>
        a.properties.status === "Failed" ||
        a.properties.status === "TimedOut"
    );
  }

  const shouldFetchInputs = (status: string): boolean => {
    if (parsed.includeInputs === true) return true;
    if (parsed.includeInputs === false) return false;
    return status === "Failed" || status === "TimedOut";
  };

  const shouldFetchOutputs = (status: string): boolean => {
    if (parsed.includeOutputs === true) return true;
    if (parsed.includeOutputs === false) return false;
    return status === "Failed" || status === "TimedOut";
  };

  const actionIO: Record<string, { inputs?: string; outputs?: string; outputsRaw?: unknown }> = {};

  const fetchPromises: Promise<void>[] = [];

  for (const action of filteredActions) {
    const status = action.properties.status;
    const needInputs = shouldFetchInputs(status) && action.properties.inputsLink?.uri;
    const needOutputs = shouldFetchOutputs(status) && action.properties.outputsLink?.uri;

    if (needInputs) {
      fetchPromises.push(
        api.fetchResourceLink(action.properties.inputsLink!.uri).then(
          (data) => {
            if (!actionIO[action.name]) actionIO[action.name] = {};
            actionIO[action.name]!.inputs = formatResourceData(data);
          },
          (err) => {
            if (!actionIO[action.name]) actionIO[action.name] = {};
            const msg = err instanceof Error ? err.message : String(err);
            actionIO[action.name]!.inputs = `(fetch error: ${msg.slice(0, 100)})`;
          }
        )
      );
    }

    if (needOutputs) {
      fetchPromises.push(
        api.fetchResourceLink(action.properties.outputsLink!.uri).then(
          (data) => {
            if (!actionIO[action.name]) actionIO[action.name] = {};
            actionIO[action.name]!.outputs = formatResourceData(data);
            actionIO[action.name]!.outputsRaw = data;
          },
          (err) => {
            if (!actionIO[action.name]) actionIO[action.name] = {};
            const msg = err instanceof Error ? err.message : String(err);
            actionIO[action.name]!.outputs = `(fetch error: ${msg.slice(0, 100)})`;
          }
        )
      );
    }
  }

  await Promise.all(fetchPromises);

  const results: RunActionResult[] = filteredActions.map((action) => {
    const props = action.properties;
    const startTime = new Date(props.startTime);
    const endTime = props.endTime ? new Date(props.endTime) : undefined;
    const io = actionIO[action.name];

    return {
      name: action.name,
      status: props.status,
      startTime: props.startTime,
      endTime: props.endTime,
      durationMs: endTime ? endTime.getTime() - startTime.getTime() : undefined,
      error:
        props.error ??
        ((props.status === "Failed" || props.status === "TimedOut") && io?.outputsRaw !== undefined
          ? deriveErrorFromOutputs(io.outputsRaw)
          : undefined),
      inputs: io?.inputs,
      outputs: io?.outputs,
      retryCount: props.retryHistory?.length,
      trackedProperties: props.trackedProperties,
    };
  });

  // The service returns actions alphabetically; a run reads in the order it happened.
  results.sort((a, b) => Date.parse(a.startTime) - Date.parse(b.startTime));

  const failedCount = results.filter(
    (r) => r.status === "Failed" || r.status === "TimedOut"
  ).length;
  const succeededCount = results.filter(
    (r) => r.status === "Succeeded"
  ).length;
  const skippedCount = results.filter((r) => r.status === "Skipped").length;

  const lines: string[] = [
    `Actions: ${results.length} total (${succeededCount} succeeded, ${failedCount} failed${skippedCount > 0 ? `, ${skippedCount} skipped` : ""})`,
  ];

  if (parsed.actionName) {
    lines[0] += ` [filtered by "${parsed.actionName}"]`;
  }

  const renderAction = (action: RunActionResult): string => {
    const duration = action.durationMs
      ? `${(action.durationMs / 1000).toFixed(1)}s`
      : "";
    const status =
      action.status === "Succeeded"
        ? "✓"
        : action.status === "Failed"
          ? "✗"
          : action.status === "TimedOut"
            ? "⏱"
            : "○";
    let line = `\n  ${status} ${action.name} ${duration}`;

    if (action.error) {
      line += `\n    Error: [${action.error.code}] ${action.error.message.slice(0, ERROR_TRUNCATE_LIMIT)}`;
    }

    if (action.inputs) {
      line += `\n    Inputs: ${action.inputs}`;
    }

    if (action.outputs) {
      line += `\n    Outputs: ${action.outputs}`;
    }

    if (action.trackedProperties && Object.keys(action.trackedProperties).length > 0) {
      line += `\n    Tracked: ${JSON.stringify(action.trackedProperties)}`;
    }

    if (action.retryCount && action.retryCount > 0) {
      line += `\n    Retries: ${action.retryCount}`;
    }

    return line;
  };

  // Skipped actions are flow control, not failures: every branch the run did
  // not take is "skipped" with an ActionConditionFailed note, and printing one
  // per action buried the single real result under 99 non-errors. Collapse
  // them to a count and names unless the caller asked about one by name.
  const detailed = parsed.actionName ? results : results.filter((r) => r.status !== "Skipped");
  for (const action of detailed) {
    lines.push(renderAction(action));
  }
  const skipped = parsed.actionName ? [] : results.filter((r) => r.status === "Skipped");
  if (skipped.length > 0) {
    const names = skipped.map((r) => r.name);
    const shown = names.slice(0, 25).join(", ");
    const more = names.length > 25 ? `, +${names.length - 25} more` : "";
    lines.push(
      `\n  ○ ${skipped.length} skipped (branches the run did not take): ${shown}${more}`,
      `    Pass actionName to see why a specific one was skipped.`
    );
  }

  // Machine-parseable mirror of the prose, emitted as MCP structuredContent (#11).
  const structured: Record<string, unknown> = {
    count: results.length,
    succeeded: succeededCount,
    failed: failedCount,
    skipped: skippedCount,
    ...(parsed.actionName ? { filteredBy: parsed.actionName } : {}),
    actions: results.map((action) => ({
      name: action.name,
      status: action.status,
      startTime: action.startTime,
      endTime: action.endTime ?? null,
      durationMs: action.durationMs ?? null,
      error: action.error ?? null,
      ...(action.inputs ? { inputs: action.inputs } : {}),
      ...(action.outputs ? { outputs: action.outputs } : {}),
      ...(action.trackedProperties && Object.keys(action.trackedProperties).length > 0
        ? { trackedProperties: action.trackedProperties }
        : {}),
      ...(action.retryCount && action.retryCount > 0 ? { retryCount: action.retryCount } : {}),
    })),
  };

  return { text: lines.join("\n"), structured };
}
